/**
 * Mill Store - Online Ordering for Bridge Club Members
 *
 * Browse products, add to cart, checkout with saved payment methods.
 * Orders are prepared for locker pickup.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useDebouncedSearch } from '../../hooks/useDebouncedSearch';
import { useNavigate } from 'react-router-dom';
import BridgeClubLayout from '../../components/BridgeClub/BridgeClubLayout';
import ProductCard, { ProductCardProduct } from '../../components/common/ProductCard';
import { bridgeClubAPI } from '../../services/bridgeClubAPI';
import '../../components/common/ProductCard.css';
import './MillStore.css';

interface Product {
  id: number;
  name: string;
  sku: string;
  description: string;
  retail_price: string;
  size_lbs?: string;
  size_unit?: string;
  category_name: string;
  is_taxable: boolean;
  brand?: string;
  form?: string;
  form_display?: string;
}

interface Category {
  id: number;
  name: string;
  slug: string;
  icon?: string;
  color?: string;
}

interface CartItem {
  id: number;
  product: number;
  product_name: string;
  product_sku: string;
  quantity: number;
  unit_price: string;
  line_total: string;
  tax_amount: string;
}

interface Cart {
  id: number;
  order_number: string;
  items: CartItem[];
  subtotal: string;
  tax_amount: string;
  total: string;
}

interface SavedPaymentMethod {
  id: number;
  type: 'card' | 'ach';
  card_type?: string;
  last_four: string;
  bank_name?: string;
  is_default: boolean;
  nickname?: string;
}

const MillStore: React.FC = () => {
  const navigate = useNavigate();
  const [view, setView] = useState<'catalog' | 'cart' | 'checkout'>('catalog');
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [cart, setCart] = useState<Cart | null>(null);
  const [paymentMethods, setPaymentMethods] = useState<SavedPaymentMethod[]>([]);
  const [selectedPaymentId, setSelectedPaymentId] = useState<number | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  // Server + client search: input binds to `searchInput` (instant); the debounced
  // `searchQuery` (400ms) drives BOTH the params.q fetch and the in-memory product
  // filter, so typing fires one settled fetch/filter after the pause.
  const { value: searchInput, setValue: setSearchInput, debounced: searchQuery } = useDebouncedSearch('', 400);
  const [sortBy, setSortBy] = useState<'name-asc' | 'price-low' | 'price-high'>('name-asc');
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    loadProducts();
  }, [selectedCategory, searchQuery]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [categoriesData, cartData] = await Promise.all([
        bridgeClubAPI.millStoreCategories(),
        bridgeClubAPI.millStoreCart(),
      ]);
      setCategories(categoriesData.categories || []);
      setCart(cartData.cart || null);
      await loadProducts();
    } catch (err: any) {
      console.error('Failed to load Mill Store data:', err);
      setError(err.message || 'Failed to load store');
    } finally {
      setLoading(false);
    }
  };

  const loadProducts = async () => {
    try {
      const params: any = {};
      if (selectedCategory) params.category = selectedCategory;
      if (searchQuery) params.q = searchQuery;

      const data = await bridgeClubAPI.millStoreCatalog(params);
      setProducts(data.products || []);
    } catch (err) {
      console.error('Failed to load products:', err);
    }
  };

  const loadCart = async () => {
    try {
      const data = await bridgeClubAPI.millStoreCart();
      setCart(data.cart || null);
    } catch (err) {
      console.error('Failed to load cart:', err);
    }
  };

  const loadPaymentMethods = async () => {
    try {
      const data = await bridgeClubAPI.getSavedPaymentMethods();
      setPaymentMethods(data || []);
      const defaultMethod = data?.find((m: SavedPaymentMethod) => m.is_default);
      if (defaultMethod) {
        setSelectedPaymentId(defaultMethod.id);
      }
    } catch (err) {
      console.error('Failed to load payment methods:', err);
    }
  };

  const handleAddToCart = async (product: Product) => {
    try {
      setProcessing(true);
      setError('');
      const data = await bridgeClubAPI.millStoreAddToCart(product.id, 1);
      setCart(data.cart);
      setSuccessMessage(`Added ${product.name} to cart`);
      setTimeout(() => setSuccessMessage(''), 2000);
    } catch (err: any) {
      setError(err.message || 'Failed to add to cart');
    } finally {
      setProcessing(false);
    }
  };

  const handleUpdateQuantity = async (itemId: number, quantity: number) => {
    try {
      setProcessing(true);
      const data = await bridgeClubAPI.millStoreUpdateCartItem(itemId, quantity);
      setCart(data.cart);
    } catch (err: any) {
      setError(err.message || 'Failed to update cart');
    } finally {
      setProcessing(false);
    }
  };

  const handleRemoveItem = async (itemId: number) => {
    try {
      setProcessing(true);
      const data = await bridgeClubAPI.millStoreRemoveCartItem(itemId);
      setCart(data.cart);
    } catch (err: any) {
      setError(err.message || 'Failed to remove item');
    } finally {
      setProcessing(false);
    }
  };

  const handleGoToCheckout = async () => {
    await loadPaymentMethods();
    setView('checkout');
  };

  const handleCheckout = async () => {
    if (!selectedPaymentId) {
      setError('Please select a payment method');
      return;
    }

    try {
      setProcessing(true);
      setError('');
      const result = await bridgeClubAPI.millStoreCheckout(selectedPaymentId);

      if (result.success) {
        setCart(null);
        navigate(`/bridge-club/mill-store/order/${result.order.order_number}`);
      } else if (result.requires_payment) {
        // Need Helcim.js for new card - redirect to payment
        setError('Please add a payment method first');
      } else {
        setError(result.error || 'Checkout failed');
      }
    } catch (err: any) {
      setError(err.message || 'Checkout failed');
    } finally {
      setProcessing(false);
    }
  };

  // Sort products
  const sortedProducts = useMemo(() => {
    const sorted = [...products];
    switch (sortBy) {
      case 'price-low':
        return sorted.sort((a, b) => parseFloat(a.retail_price) - parseFloat(b.retail_price));
      case 'price-high':
        return sorted.sort((a, b) => parseFloat(b.retail_price) - parseFloat(a.retail_price));
      default:
        return sorted.sort((a, b) => a.name.localeCompare(b.name));
    }
  }, [products, sortBy]);

  // Group by category when no filter is active
  const groupedProducts = useMemo(() => {
    if (selectedCategory || searchQuery) return null;
    const groups: Record<string, Product[]> = {};
    for (const p of sortedProducts) {
      const cat = p.category_name || 'Other';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(p);
    }
    return Object.entries(groups).map(([category, prods]) => ({ category, products: prods }));
  }, [sortedProducts, selectedCategory, searchQuery]);

  const toCardProduct = (p: Product): ProductCardProduct => ({
    id: p.id,
    name: p.name,
    brand: p.brand,
    form: p.form_display || p.form,
    category: p.category_name,
    sizeLbs: p.size_lbs,
    sizeUnit: p.size_unit,
    price: p.retail_price,
  });

  const cartItemCount = cart?.items.reduce((sum, item) => sum + item.quantity, 0) || 0;

  if (loading) {
    return (
      <BridgeClubLayout>
        <div className="mill-store-loading">
          <div className="spinner"></div>
          <p>Loading Mill Store...</p>
        </div>
      </BridgeClubLayout>
    );
  }

  return (
    <BridgeClubLayout>
      <div className="mill-store">
        {/* Header */}
        <div className="mill-store-header">
          <h1>Mill Store</h1>
          <p className="mill-store-subtitle">Order for locker pickup</p>
          {view !== 'cart' && cartItemCount > 0 && (
            <button className="cart-btn" onClick={() => setView('cart')}>
              Cart ({cartItemCount})
            </button>
          )}
        </div>

        {/* Messages */}
        {error && (
          <div className="mill-store-error">
            {error}
            <button onClick={() => setError('')}>X</button>
          </div>
        )}
        {successMessage && (
          <div className="mill-store-success">{successMessage}</div>
        )}

        {/* Catalog View */}
        {view === 'catalog' && (
          <>
            {/* Filter Bar */}
            <div className="filter-bar">
              <div className="filter-pills">
                <button
                  className={`filter-pill ${!selectedCategory ? 'active' : ''}`}
                  onClick={() => setSelectedCategory(null)}
                >
                  All
                </button>
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    className={`filter-pill ${selectedCategory === cat.id ? 'active' : ''}`}
                    onClick={() => setSelectedCategory(cat.id)}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
              <div className="filter-search-wrap">
                <span className="search-icon">&#128269;</span>
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search products..."
                  className="filter-search"
                />
              </div>
              <select
                className="filter-sort"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
              >
                <option value="name-asc">A-Z</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
              </select>
            </div>

            {/* Products Grid */}
            {products.length === 0 ? (
              <div className="no-products">No products found</div>
            ) : groupedProducts ? (
              groupedProducts.map((group) => (
                <div key={group.category} className="product-grid-section">
                  <div className="product-grid-section-header">
                    <h2>{group.category}</h2>
                    <span className="section-line" />
                    <span className="product-grid-section-count">
                      {group.products.length} item{group.products.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="pgrid">
                    {group.products.map((product) => (
                      <ProductCard
                        key={product.id}
                        product={toCardProduct(product)}
                        onAddToCart={() => handleAddToCart(product)}
                        disabled={processing}
                      />
                    ))}
                  </div>
                </div>
              ))
            ) : (
              <div className="pgrid">
                {sortedProducts.map((product) => (
                  <ProductCard
                    key={product.id}
                    product={toCardProduct(product)}
                    onAddToCart={() => handleAddToCart(product)}
                    disabled={processing}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {/* Cart View */}
        {view === 'cart' && (
          <div className="mill-store-cart">
            <button className="back-btn" onClick={() => setView('catalog')}>
              &larr; Continue Shopping
            </button>

            <h2>Your Cart</h2>

            {!cart || cart.items.length === 0 ? (
              <div className="empty-cart">
                <p>Your cart is empty</p>
                <button className="shop-btn" onClick={() => setView('catalog')}>
                  Start Shopping
                </button>
              </div>
            ) : (
              <>
                <div className="cart-items">
                  {cart.items.map((item) => (
                    <div key={item.id} className="cart-item">
                      <div className="cart-item-info">
                        <h4>{item.product_name}</h4>
                        <p className="cart-item-sku">{item.product_sku}</p>
                        <p className="cart-item-price">${parseFloat(item.unit_price).toFixed(2)} each</p>
                      </div>
                      <div className="cart-item-qty">
                        <button
                          onClick={() => handleUpdateQuantity(item.id, item.quantity - 1)}
                          disabled={processing}
                        >
                          -
                        </button>
                        <span>{item.quantity}</span>
                        <button
                          onClick={() => handleUpdateQuantity(item.id, item.quantity + 1)}
                          disabled={processing}
                        >
                          +
                        </button>
                      </div>
                      <div className="cart-item-total">
                        ${parseFloat(item.line_total).toFixed(2)}
                      </div>
                      <button
                        className="remove-btn"
                        onClick={() => handleRemoveItem(item.id)}
                        disabled={processing}
                      >
                        X
                      </button>
                    </div>
                  ))}
                </div>

                <div className="cart-summary">
                  <div className="summary-row">
                    <span>Subtotal</span>
                    <span>${parseFloat(cart.subtotal).toFixed(2)}</span>
                  </div>
                  <div className="summary-row">
                    <span>Tax</span>
                    <span>${parseFloat(cart.tax_amount).toFixed(2)}</span>
                  </div>
                  <div className="summary-row total">
                    <span>Total</span>
                    <span>${parseFloat(cart.total).toFixed(2)}</span>
                  </div>
                  <button
                    className="checkout-btn"
                    onClick={handleGoToCheckout}
                    disabled={processing}
                  >
                    Proceed to Checkout
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Checkout View */}
        {view === 'checkout' && cart && (
          <div className="mill-store-checkout">
            <button className="back-btn" onClick={() => setView('cart')}>
              &larr; Back to Cart
            </button>

            <h2>Checkout</h2>

            <div className="checkout-summary">
              <h3>Order Summary</h3>
              <div className="checkout-items">
                {cart.items.map((item) => (
                  <div key={item.id} className="checkout-item">
                    <span>{item.quantity}x {item.product_name}</span>
                    <span>${parseFloat(item.line_total).toFixed(2)}</span>
                  </div>
                ))}
              </div>
              <div className="checkout-totals">
                <div className="summary-row">
                  <span>Subtotal</span>
                  <span>${parseFloat(cart.subtotal).toFixed(2)}</span>
                </div>
                <div className="summary-row">
                  <span>Tax</span>
                  <span>${parseFloat(cart.tax_amount).toFixed(2)}</span>
                </div>
                <div className="summary-row total">
                  <span>Total</span>
                  <span>${parseFloat(cart.total).toFixed(2)}</span>
                </div>
              </div>
            </div>

            <div className="payment-methods">
              <h3>Payment Method</h3>
              {paymentMethods.length === 0 ? (
                <div className="no-payment-methods">
                  <p>No saved payment methods</p>
                  <button
                    className="add-payment-btn"
                    onClick={() => navigate('/bridge-club/profile')}
                  >
                    Add Payment Method
                  </button>
                </div>
              ) : (
                <div className="payment-options">
                  {paymentMethods.map((method) => (
                    <label key={method.id} className="payment-option">
                      <input
                        type="radio"
                        name="payment"
                        checked={selectedPaymentId === method.id}
                        onChange={() => setSelectedPaymentId(method.id)}
                      />
                      <div className="payment-details">
                        {method.type === 'card' ? (
                          <>
                            <span className="payment-icon">&#128179;</span>
                            <span>{method.card_type} ****{method.last_four}</span>
                          </>
                        ) : (
                          <>
                            <span className="payment-icon">&#127974;</span>
                            <span>{method.bank_name} ****{method.last_four}</span>
                          </>
                        )}
                        {method.is_default && <span className="default-badge">Default</span>}
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div className="pickup-info">
              <h3>Pickup Information</h3>
              <p>Your order will be ready for pickup at our locker system.</p>
              <p>You'll receive an email with your locker code when ready.</p>
            </div>

            <button
              className="place-order-btn"
              onClick={handleCheckout}
              disabled={processing || !selectedPaymentId}
            >
              {processing ? 'Processing...' : 'Place Order'}
            </button>
          </div>
        )}
      </div>
    </BridgeClubLayout>
  );
};

export default MillStore;
